import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Smartphone, RefreshCw, KeyRound, ShieldOff, MonitorX } from 'lucide-react';
import Button from '../ui/Button';
import TextInput from '../ui/TextInput';
import CheckboxInput from '../ui/CheckboxInput';
import ConfirmDialog from '../ui/ConfirmDialog';
import GenericFormDrawer from '../GenericFormDrawer';
import MfaSetupPanel from './MfaSetupPanel';
import RecoveryCodesPanel from './RecoveryCodesPanel';
import { getMe } from '../../services/authService';
import {
  confirmMfaSetup,
  disableMfa,
  getMfaStatus,
  regenerateRecoveryCodes,
  revokeTrustedBrowsers,
  sendMfaSetupCode,
  startMfaSetup,
} from '../../services/mfaService';
import { formatDate } from '../../utils/Utils';

// Profil → Hesap Güvenliği: iki adımlı doğrulama (self-servis).
//
// Değişiklik kuralları sunucudadır (MfaDataAccess): ilk kurulum e-posta koduyla, yeniden kurulum
// / kodları yenileme / kapatma mevcut doğrulayıcının koduyla. Bu bileşen yalnız adımları çizer.

const Section = styled.div`
  border-top: 1px solid #E8ECF0;
  padding-top: 16px;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const Head = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

const Title = styled.span`
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  color: ${({ theme }) => theme.colors.text.primary};
  display: inline-flex;
  align-items: center;
  gap: 8px;
`;

const Chip = styled.span`
  font-size: 11px;
  font-weight: ${({ theme }) => theme.metrics.font.weight.bold};
  padding: 3px 8px;
  border-radius: 10px;
  color: ${({ $on, theme }) => ($on ? theme.colors.secondary.dark : '#6B7280')};
  background: ${({ $on }) => ($on ? 'rgba(2, 211, 191, 0.14)' : '#F0F2F5')};
`;

const Text = styled.p`
  margin: 0;
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  line-height: 1.5;
  color: ${({ $warn, theme }) => ($warn ? theme.colors.auxiliar.danger : '#6B7280')};
`;

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const DrawerBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const LinkButton = styled.button`
  align-self: flex-start;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  color: ${({ theme }) => theme.colors.primary.main};

  &:disabled { color: #A0A8B4; cursor: default; }
`;

const RESEND_COOLDOWN_SECONDS = 60;

const digits = (value) => value.replace(/\D/g, '').slice(0, 6);

export default function MfaSecuritySection() {
  const { t, i18n } = useTranslation();
  const [status, setStatus] = useState(null);
  // null | 'setup' | 'regenerate' | 'disable'
  const [drawer, setDrawer] = useState(null);
  // setup: 'verify' (e-posta kodu ya da mevcut kod) | 'app' | 'codes'; regenerate: 'verify' | 'codes'
  const [step, setStep] = useState('verify');
  const [code, setCode] = useState('');
  const [setup, setSetup] = useState(null);
  const [codes, setCodes] = useState([]);
  const [savedCodes, setSavedCodes] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [confirmRevoke, setConfirmRevoke] = useState(false);

  const loadStatus = useCallback(() => getMfaStatus().then((res) => {
    if (res?.data?.data) setStatus(res.data.data);
  }), []);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const id = setTimeout(() => setCooldown((s) => s - 1), 1000);
    return () => clearTimeout(id);
  }, [cooldown]);

  // MFA durumu oturum yanıtında da var (mfaEnabled) — üst bilgi ve diğer ekranlar tazelensin.
  const refreshSession = () => getMe().then((res) => {
    const fresh = res?.data?.data;
    if (fresh) window.dispatchEvent(new CustomEvent('auth:refreshed', { detail: fresh }));
  });

  const sendCode = () => sendMfaSetupCode()
    .then((res) => { if (res?.data?.status) setCooldown(RESEND_COOLDOWN_SECONDS); })
    .catch(() => {});

  const open = (mode) => {
    setDrawer(mode);
    setStep('verify');
    setCode('');
    setSetup(null);
    setCodes([]);
    setSavedCodes(false);
    // İlk kurulum: doğrulama kodu e-postayla gelir — çekmece açılınca gönderilir.
    if (mode === 'setup' && !status?.enabled) sendCode();
  };

  const close = () => {
    const finished = step === 'codes';
    setDrawer(null);
    if (finished) {
      loadStatus();
      refreshSession();
    }
  };

  // GenericFormDrawer KABUK sözleşmesi: false → çekmece açık kalır (bir sonraki adım ya da
  // geçersiz girdi); fırlatılan hata (sunucu reddi) da açık tutar, mesajı interceptor gösterir.
  const handleSave = async () => {
    if (drawer === 'setup') {
      if (step === 'verify') {
        const payload = status?.enabled ? { currentCode: code.trim() } : { emailCode: code.trim() };
        const res = await startMfaSetup(payload);
        if (!res?.data?.data) return false;
        setSetup(res.data.data);
        setCode('');
        setStep('app');
        return false;
      }
      if (step === 'app') {
        const res = await confirmMfaSetup(code.trim());
        if (!res?.data?.data) return false;
        setCodes(res.data.data.recoveryCodes || []);
        setCode('');
        setStep('codes');
        return false;
      }
      // Kodlar adımı: true → çekmece onClose'u (close) çağırır; durum orada tazelenir.
      return true;
    }

    if (drawer === 'regenerate') {
      if (step === 'verify') {
        const res = await regenerateRecoveryCodes(code.trim());
        if (!res?.data?.data) return false;
        setCodes(res.data.data.recoveryCodes || []);
        setCode('');
        setStep('codes');
        return false;
      }
      // Kodlar adımı: true → çekmece onClose'u (close) çağırır; durum orada tazelenir.
      return true;
    }

    if (drawer === 'disable') {
      await disableMfa(code.trim());
      loadStatus();
      refreshSession();
      return true;
    }
    return true;
  };

  const handleRevoke = async () => {
    try {
      await revokeTrustedBrowsers();
    } catch { /* interceptor */ }
    setConfirmRevoke(false);
  };

  if (!status) return null;

  const enabled = Boolean(status.enabled);
  const codeInput = (labelKey, allowRecovery = false) => (
    <TextInput
      label={t(labelKey)}
      value={code}
      onChange={(e) => setCode(allowRecovery ? e.target.value.toUpperCase() : digits(e.target.value))}
      autoComplete="one-time-code"
      inputMode={allowRecovery ? 'text' : 'numeric'}
      required
    />
  );

  let drawerTitle = '';
  let submitLabel;
  let submitDisabled = false;
  let body = null;

  if (drawer === 'setup') {
    drawerTitle = enabled ? t('mfa.profile.reconfigureTitle') : t('mfa.profile.enableTitle');
    if (step === 'verify') {
      submitLabel = t('mfa.login.next');
      submitDisabled = code.trim().length < 6;
      body = enabled ? (
        <>
          <Text>{t('mfa.profile.reconfigureVerifyHint')}</Text>
          {codeInput('mfa.profile.currentCode', true)}
        </>
      ) : (
        <>
          <Text>{t('mfa.profile.emailCodeHint', { email: status.maskedEmail || '' })}</Text>
          {codeInput('mfa.login.emailCode')}
          <LinkButton type="button" onClick={sendCode} disabled={cooldown > 0}>
            {cooldown > 0 ? t('mfa.login.resendIn', { seconds: cooldown }) : t('mfa.login.resend')}
          </LinkButton>
        </>
      );
    } else if (step === 'app') {
      submitLabel = t('mfa.profile.verifyAndEnable');
      submitDisabled = code.length < 6;
      body = (
        <>
          <MfaSetupPanel setup={setup} />
          {codeInput('mfa.login.code')}
        </>
      );
    }
  } else if (drawer === 'regenerate') {
    drawerTitle = t('mfa.profile.regenerateTitle');
    if (step === 'verify') {
      submitLabel = t('mfa.profile.regenerateSubmit');
      submitDisabled = code.trim().length < 6;
      body = (
        <>
          <Text>{t('mfa.profile.regenerateHint')}</Text>
          {codeInput('mfa.profile.currentCode', true)}
        </>
      );
    }
  } else if (drawer === 'disable') {
    drawerTitle = t('mfa.profile.disableTitle');
    submitLabel = t('mfa.profile.disableSubmit');
    submitDisabled = code.trim().length < 6;
    body = (
      <>
        <Text $warn>{t('mfa.profile.disableHint')}</Text>
        {codeInput('mfa.profile.currentCode', true)}
      </>
    );
  }

  if (drawer && step === 'codes') {
    submitLabel = t('mfa.recovery.done');
    submitDisabled = !savedCodes;
    body = (
      <>
        <RecoveryCodesPanel codes={codes} />
        <CheckboxInput label={t('mfa.recovery.saved')} checked={savedCodes} onChange={setSavedCodes} />
      </>
    );
  }

  return (
    <Section data-testid="mfa-section">
      <Head>
        <Title>
          <Smartphone size={16} />
          {t('mfa.profile.title')}
        </Title>
        <Chip $on={enabled}>{enabled ? t('mfa.profile.statusOn') : t('mfa.profile.statusOff')}</Chip>
      </Head>

      {!status.available ? (
        <Text>{t('mfa.profile.unavailable')}</Text>
      ) : enabled ? (
        <>
          <Text>
            {t('mfa.profile.enabledSince', { date: formatDate(status.enabledAt, i18n.language) })}
            {' · '}
            {t('mfa.profile.recoveryRemaining', { count: status.recoveryCodesRemaining })}
          </Text>
          {status.required && <Text>{t('mfa.profile.requiredHint')}</Text>}
          <Actions>
            <Button variant="outline" onClick={() => open('setup')}>
              <RefreshCw size={14} />
              {t('mfa.profile.reconfigure')}
            </Button>
            <Button variant="outline" onClick={() => open('regenerate')}>
              <KeyRound size={14} />
              {t('mfa.profile.regenerate')}
            </Button>
            {status.trustBrowserAllowed && (
              <Button variant="ghost" onClick={() => setConfirmRevoke(true)}>
                <MonitorX size={14} />
                {t('mfa.profile.revokeBrowsers')}
              </Button>
            )}
            {!status.required && (
              <Button variant="ghost" onClick={() => open('disable')}>
                <ShieldOff size={14} />
                {t('mfa.profile.disable')}
              </Button>
            )}
          </Actions>
        </>
      ) : (
        <>
          <Text $warn={status.required}>
            {status.required ? t('mfa.profile.requiredNotEnabled') : t('mfa.profile.offHint')}
          </Text>
          <Actions>
            <Button onClick={() => open('setup')}>
              <Smartphone size={14} />
              {t('mfa.profile.enable')}
            </Button>
          </Actions>
        </>
      )}

      <GenericFormDrawer
        open={Boolean(drawer)}
        onClose={close}
        title={drawerTitle}
        schema={[]}
        onSave={handleSave}
        submitLabel={submitLabel}
        submitDisabled={submitDisabled}
      >
        <DrawerBody>{body}</DrawerBody>
      </GenericFormDrawer>

      <ConfirmDialog
        isOpen={confirmRevoke}
        type="info"
        title={t('mfa.profile.revokeBrowsersTitle')}
        message={t('mfa.profile.revokeBrowsersMessage')}
        confirmText={t('mfa.profile.revokeBrowsers')}
        onConfirm={handleRevoke}
        onCancel={() => setConfirmRevoke(false)}
      />
    </Section>
  );
}
